<template>
	<DxPopover :target="'#adv-textbox-' + textboxId" v-model:visible="fieldPopoverVisible" width="400px">
		<div>
			<DxTreeView ref="fieldsTreeView" :data-source="fieldsTree" key-expr="keyspec" display-expr="label" items-expr="fields" 
				:search-enabled="true" :search-expr="['keyspec', 'label']" :search-value="fieldsTreeSearchValue" no-data-text="No fields found."
				@item-click="fieldsTreeItemClick">
				<template #itemTemplate="item">
					<div v-if="item.data.custom">&#123;{{ item.data.keyspec }}&#125; <span class="adv-textbox-sub">({{ item.data.title }})</span></div>
					<div v-else-if="item.data.fieldtype">{{ item.data.label }} <span class="adv-textbox-sub">({{ item.data.field }})</span></div>
					<div v-else>{{ item.data.type }} <span class="adv-textbox-sub">({{ item.data.type }})</span></div>
				</template>
			</DxTreeView>
		</div>
	</DxPopover>
	<div class="adv-textbox" :id="'adv-textbox-' + textboxId">
		<DxTextBox :label="props.label" :value="props.modelValue" @update:value="emit('update:modelValue', $event)" @key-up="textboxKeyUp">
			<DxButton name="insertFieldButton" location="after" :options="insertFieldButtonOptions" />
		</DxTextBox>
	</div>
</template>

<script setup>
/* eslint-disable */
import { ref, defineProps, computed, defineEmits, nextTick } from 'vue';
import { DxTextBox, DxButton } from 'devextreme-vue/text-box';
import { DxPopover } from 'devextreme-vue/popover';
import { DxTreeView } from 'devextreme-vue/tree-view';
import DataSource from 'devextreme/data/data_source';
import { SchemaCache } from '../schemas.js';

const props = defineProps([
	'label',
	'modelValue',
	'customTokens',
]);

const emit = defineEmits(['update:modelValue']);

const fieldPopoverVisible = ref(false);
const fieldsTreeView = ref(null);
const fieldsTreeSearchValue = ref('');

// Generate a unique ID to be used as DOM ID for the popover.
const textboxId = ref(crypto.randomUUID().replaceAll('-', ''));

const insertFieldButtonOptions = {
	icon: 'datafield',
	stylingMode: 'text',
	onClick: () => {
		fieldPopoverVisible.value = !fieldPopoverVisible.value;
	},
};

// Generate the fields tree, housing all possible tokens from fields we can insert.
const fieldsTree = new DataSource({
	loadMode: 'raw',
	key: 'keyspec',
	load: async () => {
		await SchemaCache.waitForData();
		return SchemaCache.getAllSchemasExceptRelations().filter(s => s.fields.length > 0);
	},
	postProcess: (data) => {
		// Add our custom tokens at the top of the field tree as well directly.
		if (props.customTokens) {
			for (const [key, description] of Object.entries(props.customTokens)) {
				data.unshift({ custom: true, keyspec: key, title: description });
			}
		}
		return data;
	},
});

const textboxKeyUp = ({event}) => {
	if (event.key == '{') {
		fieldPopoverVisible.value = true;
	} else if (event.key == '}') {
		fieldPopoverVisible.value = false;
	} else if (event.key == 'Escape') {
		fieldPopoverVisible.value = false;
	} else {
		// Autofill the search with the currently typing keyspec. This is only valid if it is not completed,
		// i.e. '{name' is valid, but '{names}' is not.
		let currentValue = event.target.value;		// We need to get the value directly from event target as the above values won't be updated yet.
		const lastBraceEnter = currentValue.lastIndexOf('{');
		const lastBraceExit = currentValue.lastIndexOf('}');
		if (lastBraceEnter != -1 && (lastBraceExit < lastBraceEnter || lastBraceExit == -1)) {
			fieldsTreeSearchValue.value = currentValue.substring(lastBraceEnter + 1);
		}
	}
}

// Add a given keyspec (e.g. 'customer.name') to the textbox value, taking into account previous text/braces typed in already.
function addKeyspecToTextbox(keyspec) {
	let newValue = props.modelValue;
	// Three cases: we end with a brace, we end with a partial keyspec, or we haven't started anything yet.
	if (newValue.endsWith('{')) {
		newValue += keyspec + '}';
	} else {
		const lastBraceEnter = newValue.lastIndexOf('{');
		const lastBraceExit = newValue.lastIndexOf('}');
		if (lastBraceEnter != -1 && (lastBraceExit < lastBraceEnter || lastBraceExit == -1)) {
			newValue = newValue.substring(0, lastBraceEnter + 1) + keyspec + '}';
		} else {
			newValue += '{' + keyspec + '}';
		}
	}
	// Required, or some part of our underlying parent components (DxForm?) causes major problems 
	nextTick(() => {
		emit('update:modelValue', newValue);
	});
}

const fieldsTreeItemClick = ({itemData, itemElement}) => {
	const isLeaf = 'fieldtype' in itemData;
	const isCustom = 'custom' in itemData && itemData.custom;
	if (isCustom) {
		fieldPopoverVisible.value = false;
		addKeyspecToTextbox(itemData.keyspec);
	} else if (isLeaf) {
		fieldPopoverVisible.value = false;
		addKeyspecToTextbox(itemData.keyspec);
	} else {
		const isExpanded = itemElement.nextSibling?.classList.contains('dx-treeview-node-container-opened') ?? false;
		if (!isExpanded) {
			fieldsTreeView.value.instance.expandItem(itemData.keyspec);
		} else {
			fieldsTreeView.value.instance.collapseItem(itemData.keyspec);
		}
	}
};
</script>

<style lang="scss">
.adv-textbox-sub {
	font-size: 12px;
	color: #aaa;
	font-style: italic;
}
</style>
